import type { FieldConfigurationDto } from '@tempo-io/fim3-dto';
import { createContext, type ReactNode, type RefObject, useContext, useRef } from 'react';
import { type CustomFieldInputHandle } from '../components/custom-fields/CustomFieldInput';

type FieldValidationContextType = {
  addRef: (id: number, ref: RefObject<CustomFieldInputHandle | null>) => void;
  validateAll: () => boolean;
  validate: (id: number) => boolean;
  getCurrentState: (id: number) => { values: string[], required: boolean, fieldConfiguration: FieldConfigurationDto } | undefined;
};

export const FieldValidationContext = createContext<FieldValidationContextType>({
  validateAll: () => {
    return true;
  },
  validate: () => {
    return true;
  },
  addRef: () => {
    return;
  },
  getCurrentState: () => {
    return undefined;
  },
});

type FieldValidationProviderProps = {
  children: ReactNode;
};

export const useFieldValidationContext = () => useContext(FieldValidationContext);

export const FieldValidationProvider = ({ children }: FieldValidationProviderProps) => {
  const fieldRefs = useRef<Map<number, RefObject<CustomFieldInputHandle | null>>>(new Map());

  const validateAll = () => {
    let valid = true;
    fieldRefs.current.forEach((ref) => {
      if (ref.current) {
        const isThisOneValid = ref.current.validate();
        valid = valid && isThisOneValid;
      }
    });
    return valid;
  };

  const validate = (id: number) => {
    let valid = true;
    const ref = fieldRefs.current.get(id);
    if (ref?.current) {
      valid = ref.current.validate();
    }
    return valid;
  };

  const getCurrentState = (id: number) => {
    const ref = fieldRefs.current.get(id);
    if (ref?.current) {
      return ref.current.currentState();
    }
    return undefined;
  };

  const addRef = (id: number, ref: RefObject<CustomFieldInputHandle | null>) => {
    fieldRefs.current.set(id, ref);
  };

  const context = { validate, validateAll, addRef, getCurrentState};

  return (
    <FieldValidationContext.Provider value={context}>{children}</FieldValidationContext.Provider>
  );
};
